import { useEffect, useState } from 'react'
import { Check, Loader2, MinusCircle, RefreshCw, ShieldAlert, X } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { getUser } from '@/features/users/api'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { GateCatalogueEntry, GateResult, RequestKindCode } from '@/shared/api/types'
import { GATE_LABEL, GATE_ORDER, gateCatalogue, recheckTransfer, waiveGate } from './api'

/**
 * The sixteen eligibility gates, each reported separately.
 *
 * The whole reason the backend names sixteen gates instead of returning
 * "ineligible" is so this panel can tell an operator **which** check failed and
 * therefore what to do about it. A single "ineligible" verdict sends someone to
 * ask an engineer; "Target has capacity — 1,412 registered against a ceiling of
 * 665" sends them to pick a different station.
 *
 * So every gate renders, always, including the ones that passed. A checklist you
 * can only see when it fails teaches nobody what is being checked, and the list
 * of checks is itself the argument that this workflow is careful.
 *
 * ## Waiving
 *
 * A waiver is not a shortcut, it is a **recorded exception with a name on it**.
 * It needs `relocation:waive`, a mandatory reason, and it stays visible on the
 * request forever — this panel keeps showing the gate as waived, by whom, and
 * why, rather than folding it back into "passed". The one case it exists for is
 * a dissolved station, where the voter did not choose to move and cannot consent
 * to something that already happened to their station.
 */
export function EligibilityPanel({
  requestId, kind = 'TRANSFER', gates, onChanged,
}: {
  requestId: string
  /** Decides which gates apply. A registration does not run the transfer-only ones. */
  kind?: RequestKindCode
  gates: GateResult[]
  onChanged: () => void
}) {
  const canWaive = useCan('relocation:waive')
  // Re-running the checks is not a state transition — it re-evaluates the same
  // request against today's data. Either the person who drafted it or the person
  // about to review it may need to.
  // Two separate calls, then the OR. `a() || b()` short-circuits, and a hook that is
  // sometimes not called changes the hook count between renders — React throws
  // "rendered more hooks than during the previous render" the moment the session's
  // authorities change under a mounted panel.
  const canDraft = useCan('relocation:create')
  const canReview = useCan('relocation:review')
  const canRecheck = canDraft || canReview
  const [waiving, setWaiving] = useState<GateResult | null>(null)
  const [rechecking, setRechecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const catalogue = useGateCatalogue()

  /**
   * Which gates this request *should* have, and in what order.
   *
   * The server decides, in three fallbacks:
   *
   *  1. The gates it actually reported, in the order it ran them. Once a request has
   *     been checked this is the truth and nothing else is consulted.
   *  2. Before the first check — a DRAFT reports none — the catalogue, filtered to this
   *     request's kind, so the operator can see what is coming.
   *  3. If the catalogue endpoint is not there (a backend older than prompt 28), this
   *     bundle's own order.
   *
   * What this must NOT do is render a fixed list of sixteen regardless: a gate that does
   * not apply to a registration would sit there forever as "not checked", which reads as
   * an outstanding problem rather than as a gate that was never going to run.
   */
  const expected: string[] =
    gates.length > 0 ? gates.map((g) => g.gate)
      : catalogue
        ? catalogue.filter((c) => c.appliesTo.includes(kind)).map((c) => c.gate)
        : GATE_ORDER

  const byGate = new Map(gates.map((g) => [g.gate, g]))

  const failing = gates.filter((g) => g.result === 'FAIL')
  const skipped = gates.filter((g) => g.result === 'SKIPPED')

  return (
    <Panel
      title="Eligibility"
      description={`${gates.filter((g) => g.result === 'PASS').length} passed · ${failing.length} failed · ${gates.filter((g) => g.result === 'WAIVED').length} waived`}
      right={canRecheck ? (
        <button
          type="button"
          className="erp-btn"
          disabled={rechecking}
          onClick={async () => {
            setRechecking(true); setError(null)
            try { await recheckTransfer(requestId); onChanged() } catch (e) { setError(explain(e)) }
            finally { setRechecking(false) }
          }}
        >
          {rechecking
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />} Re-run checks
        </button>
      ) : undefined}
    >
      <ErrorStrip message={error} className="mb-3 border rounded-md" />
      {failing.length === 0 && skipped.length > 0 && (
        <div className="mb-3">
          <Note tone="info">
            A skipped gate is not a pass — it means a dependency was unavailable when the check
            ran. Skipped gates block approval, so re-check before reviewing.
          </Note>
        </div>
      )}

      <ul className="divide-y divide-border">
        {expected.map((code) => {
          const g = byGate.get(code) ?? null
          const meta = GATE_LABEL[code]
          return (
            <GateRow
              key={code}
              code={code}
              label={meta?.label ?? humanise(code)}
              hint={meta?.hint
                ?? 'This build does not recognise this gate — the backend is newer than the app.'}
              result={g}
              canWaive={canWaive}
              onWaive={() => setWaiving(g ?? { gate: code, result: 'SKIPPED', checkedAt: '' })}
            />
          )
        })}
      </ul>

      {waiving && (
        <WaiveModal
          requestId={requestId}
          gate={waiving.gate}
          onClose={() => setWaiving(null)}
          onWaived={() => { setWaiving(null); onChanged() }}
        />
      )}
    </Panel>
  )
}

/**
 * The gate catalogue, fetched once per session and shared.
 *
 * It is reference data — it changes when the backend is redeployed, not while someone is
 * looking at a request — so a module-level promise is the right cache, and a failure is
 * remembered as "not available" rather than retried on every render.
 */
let cataloguePromise: Promise<GateCatalogueEntry[] | null> | null = null

function useGateCatalogue() {
  const [entries, setEntries] = useState<GateCatalogueEntry[] | null>(null)
  useEffect(() => {
    cataloguePromise ??= gateCatalogue().catch(() => null)
    let alive = true
    void cataloguePromise.then((c) => { if (alive) setEntries(c) })
    return () => { alive = false }
  }, [])
  return entries
}

function GateRow({
  code, label, hint, result, canWaive, onWaive,
}: {
  code: string
  label: string
  hint: string
  result: GateResult | null
  canWaive: boolean
  onWaive: () => void
}) {
  const verdict = result?.result
  return (
    <li className="py-2.5 flex items-start gap-2.5">
      <Verdict result={verdict} />
      <div className="min-w-0 flex-1">
        <div className="text-erp-sm font-medium flex items-center gap-2 flex-wrap">
          {label}
          {verdict === 'WAIVED' && (
            <span className="text-erp-xs px-1.5 py-0.5 rounded border border-amber-400/60 text-amber-800 dark:text-amber-300">
              waived
            </span>
          )}
        </div>
        <p className="text-erp-xs text-muted-foreground mt-0.5">{hint}</p>
        {result?.detail && (
          <p className="text-erp-xs mt-1 font-mono break-words">{result.detail}</p>
        )}
        {verdict === 'WAIVED' && result?.waiverReason && (
          <p className="text-erp-xs text-amber-800 dark:text-amber-300 mt-1">
            Waived{result.waivedBy && <> by <Who id={result.waivedBy} /></>}: {result.waiverReason}
          </p>
        )}
        {result?.checkedAt && (
          <p className="text-erp-xs text-muted-foreground mt-0.5">{fmtDateTime(result.checkedAt)}</p>
        )}
      </div>
      {/* Only a genuine failure is waivable — waiving a pass is meaningless, and
          offering the button anyway invites someone to try. */}
      {canWaive && verdict === 'FAIL' && (
        <button type="button" className="erp-btn shrink-0" onClick={onWaive}>
          <ShieldAlert className="h-3.5 w-3.5" /> Waive
        </button>
      )}
      <span className="sr-only">{code}</span>
    </li>
  )
}

/**
 * A waiver is an exception with a name on it, so the name is shown — not the
 * uuid. The lookup needs `user:read`; when the viewer does not hold it the id
 * still appears, because "waived by someone" is worse than an unresolved id.
 */
function Who({ id }: { id: string }) {
  const u = useLoad(() => getUser(id), [id])
  return <span className="font-medium">{u.data?.name ?? id.slice(0, 8)}</span>
}

function Verdict({ result }: { result?: string }) {
  const base = 'h-4 w-4 shrink-0 mt-0.5'
  if (result === 'PASS') return <Check className={cn(base, 'text-emerald-600')} aria-label="passed" />
  if (result === 'FAIL') return <X className={cn(base, 'text-red-600')} aria-label="failed" />
  if (result === 'WAIVED') return <ShieldAlert className={cn(base, 'text-amber-600')} aria-label="waived" />
  return <MinusCircle className={cn(base, 'text-muted-foreground/50')} aria-label="not checked" />
}

function WaiveModal({
  requestId, gate, onClose, onWaived,
}: {
  requestId: string
  gate: string
  onClose: () => void
  onWaived: () => void
}) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const meta = GATE_LABEL[gate]

  return (
    <Modal
      open
      onClose={onClose}
      title={`Waive: ${meta?.label ?? humanise(gate)}`}
      description="A waiver is a recorded exception with your name on it. It stays visible on this request permanently."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={reason.trim().length < 5 || saving}
            onClick={async () => {
              setSaving(true); setError(null)
              try {
                await waiveGate(requestId, gate, reason.trim())
                onWaived()
              } catch (e) { setError(explain(e)); setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Waive this check
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />
      {meta && <p className="text-erp-sm text-muted-foreground mb-3">{meta.hint}</p>}
      <label className="block">
        <span className="text-erp-xs text-muted-foreground">Why is this acceptable here?</span>
        <textarea
          className="erp-input w-full mt-1 min-h-[80px]"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="The station was dissolved by gazette notice 1234 of 3 March; the voter could not consent to a move that had already happened."
        />
      </label>
      <p className="text-erp-xs text-muted-foreground mt-2">
        Whoever reviews this request will read your reason. Write it for them.
      </p>
    </Modal>
  )
}
